import { useTranslation } from 'react-i18next'

import { RoutePath } from '../../../layout/Routes'
import { useBreadcrumbs } from '../../../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'

import { Card, Flex, Grid, Loader, Button, Heading, Text, Badge, Box } from '@percent/lemonade'
import { env } from '@percent/partner-dashboard/env'
import { OpportunityStatus } from '@percent/partner-dashboard/services/workplaceGiving/workplaceGivingService.types'
import { ErrorView } from '@percent/partner-dashboard/common/components'
import {
  POSTHOG_FEATURE_FLAGS,
  useDidMountEffect,
  useQuery,
  useQueryList,
  usePostHogFeatureFlag,
  useRequiredParams
} from '@percent/partner-dashboard/common/hooks'
import { getFormattedDate } from '@percent/partner-dashboard/common/library/utility/date'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import styles from './VolunteeringSessionsOpportunityContainer.module.scss'
import { VolunteeringSessionsTable } from './VolunteeringSessionsTable/VolunteeringSessionsTable'
import { FeaturedStarTooltip } from '@percent/partner-dashboard/common/components/FeaturedStarTooltip/FeaturedStarTooltip'

export function VolunteeringSessionsOpportunityContainer() {
  const { t } = useTranslation()
  const { seriesOpportunityId } = useRequiredParams()
  const { workplaceGivingService } = useServices()
  const isDraftsEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.PD_OPPORTUNITY_DRAFTS)

  const [{ data: parentData, errorMessage: parentDetailsErrorMessage, isLoading: isParentDataLoading }] = useQuery(
    workplaceGivingService.getVolunteeringOpportunityDetails,
    seriesOpportunityId
  )

  const sessionStatuses = isDraftsEnabled
    ? [OpportunityStatus.DRAFT, OpportunityStatus.ACTIVE, OpportunityStatus.CLOSED]
    : [OpportunityStatus.ACTIVE, OpportunityStatus.CLOSED]

  const [{ data, isLoading, errorMessage, totalResults }, { query, nextPage, previousPage, refresh }] = useQueryList(
    workplaceGivingService.getVolunteeringOpportunitiesList,
    {
      parentId: seriesOpportunityId,
      pageSize: 25,
      status: sessionStatuses
    }
  )

  useDidMountEffect(() => {
    if (!isDraftsEnabled) {
      return
    }

    query({
      parentId: seriesOpportunityId,
      pageSize: 25,
      status: [OpportunityStatus.DRAFT, OpportunityStatus.ACTIVE, OpportunityStatus.CLOSED]
    })
  }, [isDraftsEnabled, seriesOpportunityId, query])

  const [{ data: featuredData, isLoading: isFeaturedDataLoading }] = useQuery(
    workplaceGivingService.getFeaturedItemSummary,
    seriesOpportunityId
  )

  useBreadcrumbs([
    {
      label: t('breadcrumb.volunteering'),
      path: RoutePath.WORKPLACE_GIVING_VOLUNTEERING
    },
    {
      label: parentData?.name
    }
  ])

  if (isParentDataLoading) {
    return <Loader />
  }

  if (!parentData || parentDetailsErrorMessage || !!errorMessage) {
    return (
      <Card>
        <Flex w="100%" justify="center">
          <ErrorView errorMessage={parentDetailsErrorMessage || errorMessage} />
        </Flex>
      </Card>
    )
  }

  return (
    <>
      <Card className={styles.parentDetailsWrapper}>
        <Grid gutter="l">
          <Grid.Col span={12}>
            <Flex direction="row" justify="space-between" align="center">
              <Flex gap={16} align="center">
                <Heading level="h3">{parentData.name}</Heading>
                <Flex data-testid="parent-opportunity-status">
                  {parentData.status === OpportunityStatus.ACTIVE ? (
                    <Badge variant="positive">{t('status.active')}</Badge>
                  ) : (
                    <Badge variant="critical">{t(`status.${parentData.status}`)}</Badge>
                  )}
                </Flex>
                {isFeaturedDataLoading && (
                  <Box className={styles.featuredStarLoader}>
                    <Loader isFullScreen={false} />
                  </Box>
                )}
                {!isFeaturedDataLoading && featuredData && (
                  <Box className={styles.featuredStar}>
                    <FeaturedStarTooltip
                      featured={{
                        featuredItemId: seriesOpportunityId,
                        country: featuredData.countries
                      }}
                    />
                  </Box>
                )}
              </Flex>
              <Flex>
                <Button
                  variant="secondary"
                  size="medium"
                  data-testid="view-main-event-button"
                  onClick={() =>
                    window.open(
                      `${env.NX_PUBLIC_WPG_ADDRESS}/volunteer/opportunities/${parentData.id}`,
                      '_blank',
                      'noopener noreferrer'
                    )
                  }
                >
                  {t('typography.volunteering.mainEvent')}
                </Button>
              </Flex>
            </Flex>
          </Grid.Col>
        </Grid>
        <Grid gutter="xl">
          <Grid.Col span={12}>
            <Flex direction="row" gap={16}>
              <div className={styles.detailBox}>
                <Text className={styles.label} size="small">
                  {t('table.header.nonprofit')}
                </Text>
                <Text fontWeight="bold" textWrap>
                  {parentData.organisation?.name ?? t('status.notAvailable')}
                </Text>
              </div>
              <div className={styles.detailBox}>
                <Text className={styles.label} size="small">
                  {t('table.header.organizer')}
                </Text>
                <Flex direction="column">
                  <Text fontWeight="bold">{parentData.organiser?.fullName ?? t('status.notAvailable')}</Text>
                  {parentData.organiser?.email && (
                    <Text size="small" textWrap>
                      {parentData.organiser.email}
                    </Text>
                  )}
                </Flex>
              </div>
              <div className={styles.detailBox}>
                <Text className={styles.label} size="small">
                  {t('table.header.createdAt')}
                </Text>
                <Text fontWeight="bold">{getFormattedDate(parentData.createdAt)}</Text>
              </div>
            </Flex>
          </Grid.Col>
        </Grid>
      </Card>
      <VolunteeringSessionsTable
        data={data}
        isLoading={isLoading}
        totalResults={totalResults}
        errorMessage={errorMessage}
        nextPage={nextPage}
        previousPage={previousPage}
        refresh={refresh}
      />
    </>
  )
}
